import { Link } from 'react-router-dom'

const guide = {
  slug: 'lock-safesearch-and-block-google-images',
  category: 'device',
  kicker: 'Search · Images',
  title: 'How to lock SafeSearch and block explicit Google Images, on every device',
  seoTitle: 'Lock SafeSearch & Block Google Images (iPhone, Mac, PC)',
  description: `SafeSearch is a toggle you can flip back. How to enforce it at DNS level for Google, Bing, DuckDuckGo and YouTube so it can't be switched off, on every device.`,
  published: '2026-09-03',
  minutes: 6,
  intro: `You can block every adult site on the internet and still see
          explicit results in Google Images or a YouTube search, because
          search engines aren't adult sites. SafeSearch exists for exactly
          this, and it has one problem: it's a toggle, and you know where it
          is. This guide shows how to enforce it at a level you can't reach
          from the search page, across every browser on the device.`,
  next: ['dns-porn-blocker', 'how-to-block-porn-on-chrome', 'porn-blocker-you-cant-uninstall'],
  body: (
    <>
      <h2>What SafeSearch does, and doesn't</h2>
      <p>
        With SafeSearch on, Google filters explicit images, videos and
        websites out of results. Bing, DuckDuckGo and YouTube (as Restricted
        Mode) have equivalents. It's a results filter: it doesn't stop you
        typing a site's address, and it doesn't block anything outside the
        search engine. It's a good layer on top of a site filter, not a
        replacement for one.
      </p>

      <h2>The toggle, and why it doesn't hold</h2>
      <p>
        Google's setting lives at <code>google.com/safesearch</code> and in
        every results page under Settings. Logged in, it follows your Google
        account; logged out, it follows the browser. Both are yours. A
        private window starts unfiltered. Bing and DuckDuckGo work the same
        way. The toggle is fine until the night you turn it off.
      </p>

      <h2>Enforcing it at DNS level</h2>
      <p>
        Search engines publish a way for networks and devices to force
        SafeSearch that doesn't depend on any setting: a special address
        that only ever serves filtered results.
      </p>
      <ul>
        <li><b>Google:</b> when <code>www.google.com</code> (and the country domains) resolve to <code>forcesafesearch.google.com</code>, every Google search from that device is filtered, in any browser, any profile, incognito included.</li>
        <li><b>YouTube:</b> resolving <code>www.youtube.com</code> to <code>restrict.youtube.com</code> forces strict Restricted Mode; <code>restrictmoderate.youtube.com</code> forces the moderate one.</li>
        <li><b>Bing:</b> <code>www.bing.com</code> → <code>strict.bing.com</code>.</li>
        <li><b>DuckDuckGo:</b> <code>duckduckgo.com</code> → <code>safe.duckduckgo.com</code>.</li>
      </ul>
      <p>
        This is done by the DNS resolver the device uses, so it applies
        before any browser gets a say. It's the same layer as a DNS porn
        blocker, and the same resolvers usually offer both:
        <Link to="/guides/dns-porn-blocker"> DNS porn blockers explained</Link>.
      </p>

      <h2>What "enforced" looks like on the screen</h2>
      <p>
        Google shows a small lock: "SafeSearch is locked by your network
        administrator" or similar wording, and the setting can't be changed
        from the page. YouTube says Restricted Mode is enabled by the network.
        Image search returns filtered results only.
      </p>

      <h2>Setting it up on each device</h2>
      <p>
        You can do this yourself on a router or in a device's DNS settings if
        the resolver you pick supports it, with the usual limit: you set it,
        you can unset it. The router steps are in
        <Link to="/guides/how-to-block-porn-on-wifi-router"> the router guide</Link>.
      </p>
      <p>
        SELFCNTRL enforces safe search on every locked device as part of the
        filter: Google, Bing, DuckDuckGo and YouTube Restricted Mode, in
        every browser and in the apps, with no setting to toggle. On iPhone
        and iPad it's carried by a supervised profile that can't be removed
        and forbids VPNs; on Mac and Windows by the filter app tied to your
        account. Setup is ten minutes, once:
        <Link to="/guides/how-to-block-porn-on-iphone"> the iPhone guide</Link>.
      </p>

      <h2>The honest limits</h2>
      <ul>
        <li>SafeSearch is a classifier. It catches most explicit results, not all, and it occasionally over-filters. It's a layer, not a wall; the site filter underneath is what refuses the destination.</li>
        <li>Enforced YouTube Restricted Mode is strict: it hides comments and some mainstream videos too. Most people accept it; if you don't, you keep the site filter and drop the YouTube part.</li>
        <li>Search engines that don't publish an enforcement address can't be forced this way. Blocking them entirely at DNS is the alternative.</li>
      </ul>

      <h2>Side by side</h2>
      <table>
        <thead>
          <tr><th></th><th>SafeSearch toggle</th><th>DNS enforcement you set</th><th>DNS enforcement, account-locked</th></tr>
        </thead>
        <tbody>
          <tr><td>Filters Google, Bing, DDG results</td><td>Yes, per account/browser</td><td>Yes, device-wide</td><td>Yes, device-wide</td></tr>
          <tr><td>Survives incognito / logout</td><td>No</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>YouTube Restricted Mode enforced</td><td>Separate toggle</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>Can be switched off tonight</td><td>Yes</td><td>Yes, in settings</td><td>No — 24-hour delay</td></tr>
        </tbody>
      </table>
    </>
  ),
  faq: [
    {
      q: 'How do I lock SafeSearch so it can\'t be turned off?',
      a: `Not from the Google settings page: that toggle is always yours.
          Enforce it at DNS level, by resolving www.google.com to
          forcesafesearch.google.com. Done by a resolver you can't change on
          impulse, it applies to every browser and can't be switched off
          from the page.`
    },
    {
      q: 'Does locked SafeSearch block Google Images completely?',
      a: `It filters explicit results out of Google Images; it doesn't
          disable image search. If you want images.google.com gone entirely,
          block the domain at DNS level from your account.`
    },
    {
      q: 'Can I force YouTube Restricted Mode on iPhone?',
      a: `Yes, at DNS level: www.youtube.com resolving to restrict.youtube.com
          enforces strict Restricted Mode in Safari, Chrome and the YouTube
          app. SELFCNTRL does this on every locked device.`
    },
    {
      q: 'Why does Google say SafeSearch is locked by my network?',
      a: `Because the device is resolving Google through the enforcement
          address. That's the expected message when SafeSearch is enforced
          at DNS level, by a router, a resolver or a filter like SELFCNTRL.`
    }
  ]
}

export default guide
